/* Hallmark · component: setup progress rail · genre: editorial · theme: host_ui.css (designed-as-app)
 * states: default · hover · focus · active · disabled · loading · error · success
 * contrast: token pairs verified by spec/lib/host_theme_spec.rb (4.5:1 text, 3:1 ring)
 */
import React from "react";
import { CheckIcon, LoaderCircleIcon } from "lucide-react";

import { Progress } from "@javascript/components/ui/progress";
import { cn } from "@javascript/components/utils";

export type SetupStepState = "upcoming" | "current" | "complete";

export type SetupStep = {
  label: string;
  state: SetupStepState;
};

export type ForcedState = "hover" | "focus" | "active" | "disabled" | "loading" | "error" | "success";

function derivedProgressValue(steps: readonly SetupStep[]): number {
  const currentIndex = steps.findIndex((step) => step.state === "current");
  if (currentIndex === -1) return 100;
  return Math.round((100 * (currentIndex + 1)) / steps.length);
}

function markerClasses(state: SetupStepState, forcedState?: ForcedState): string {
  const base =
    "grid size-6 shrink-0 place-items-center rounded-full border text-xs font-medium transition-[transform,background-color,border-color,color] group-active:scale-95 group-focus-visible:ring-2 group-focus-visible:ring-ring/50 group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-background";

  if (state === "complete") {
    return cn(base, "border-success/40 bg-success/10 text-success");
  }

  if (state === "current") {
    if (forcedState === "error") {
      return cn(base, "border-destructive bg-destructive text-destructive-foreground");
    }

    return cn(
      base,
      "border-primary bg-primary text-primary-foreground group-hover:bg-primary/90",
      forcedState === "hover" && "bg-primary/90",
      forcedState === "focus" && "ring-2 ring-ring/50 ring-offset-2 ring-offset-background",
      forcedState === "active" && "scale-95",
    );
  }

  return cn(
    base,
    "border-border text-muted-foreground group-hover:border-primary/50",
    forcedState === "hover" && "border-primary/50",
    forcedState === "focus" && "ring-2 ring-ring/50 ring-offset-2 ring-offset-background",
    forcedState === "active" && "scale-95",
  );
}

function stateWord(state: SetupStepState, forcedState?: ForcedState): string | null {
  if (state === "complete") return "Completed";
  if (state !== "current") return null;
  if (forcedState === "loading") return "Checking…";
  if (forcedState === "error") return "Needs attention";
  return "In progress";
}

export function SetupProgressRail({
  steps,
  forcedState,
  progressValue,
  ariaLabel = "Invitation setup",
}: {
  steps: readonly SetupStep[];
  forcedState?: ForcedState;
  progressValue?: number;
  ariaLabel?: string;
}) {
  const allComplete = forcedState === "success" || steps.every((step) => step.state === "complete");
  const value = allComplete ? 100 : progressValue ?? derivedProgressValue(steps);

  return (
    <nav
      aria-label={ariaLabel}
      aria-disabled={forcedState === "disabled" || undefined}
      data-slot="setup-progress-rail"
      className={cn("group/rail", forcedState === "disabled" && "opacity-50")}
    >
      <Progress value={value} aria-label="Invitation setup progress" />
      <ol className={cn("mt-3 grid gap-3", steps.length === 2 ? "grid-cols-2" : "grid-cols-3")}>
        {steps.map((step, index) => {
          const state = allComplete ? "complete" : step.state;
          const isCurrent = state === "current";
          const loading = isCurrent && forcedState === "loading";
          const failed = isCurrent && forcedState === "error";

          return (
            <li
              key={step.label}
              aria-current={isCurrent ? "step" : undefined}
              className="group flex items-center gap-2.5"
            >
              <span className={markerClasses(state, forcedState)}>
                {state === "complete" ? (
                  <CheckIcon aria-hidden className="size-3.5" />
                ) : loading ? (
                  <LoaderCircleIcon aria-hidden className="size-3.5 animate-spin motion-reduce:animate-none" />
                ) : (
                  index + 1
                )}
              </span>
              <span className="min-w-0">
                <span
                  className={cn(
                    "block truncate text-sm font-medium",
                    state === "upcoming" ? "text-muted-foreground" : "text-foreground",
                    failed && "text-destructive",
                  )}
                >
                  {step.label}
                </span>
                {stateWord(state, forcedState) && (
                  <span
                    className={cn(
                      "block text-xs",
                      state === "complete" ? "text-success" : "text-muted-foreground",
                      failed && "text-destructive",
                    )}
                  >
                    {stateWord(state, forcedState)}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
